/**
 * Angular-compatible number bound check (filter-sort.service checkNumber).
 * @param {number} val
 * @param {{ above?: number, below?: number, equals?: number }} bound
 * @returns {boolean}
 */
export function matchesNumberBound(val, bound) {
  if (!bound) return true;
  if (Number.isNaN(val)) return false;
  if (typeof bound.equals === "number") {
    return val === bound.equals;
  }
  const hasAbove = typeof bound.above === "number";
  const hasBelow = typeof bound.below === "number";
  if (hasAbove && hasBelow) {
    return val >= bound.above && val <= bound.below;
  }
  if (hasAbove) return val >= bound.above;
  if (hasBelow) return val <= bound.below;
  return true;
}

/**
 * Build a numberBound from AdvancedFilterModal operators.
 * @param {'above'|'below'|'equals'|'between'|''} operator
 * @param {number|''} value
 * @param {number|''} value2
 */
export function buildNumberBoundFromOperator(operator, value, value2) {
  if (
    !operator ||
    value === "" ||
    value == null ||
    Number.isNaN(Number(value))
  ) {
    return null;
  }
  const n = Number(value);
  if (operator === "equals") return { equals: n };
  if (operator === "above") return { above: n };
  if (operator === "below") return { below: n };
  if (operator === "between") {
    if (value2 === "" || value2 == null || Number.isNaN(Number(value2)))
      return null;
    const n2 = Number(value2);
    return { above: Math.min(n, n2), below: Math.max(n, n2) };
  }
  return null;
}

/** Local calendar-day ms (Angular Luxon startOf('day')). Missing/invalid → null. */
export function toDayMs(val) {
  if (val == null || val === "") return null;
  if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}/.test(val)) {
    const [y, m, d] = val.slice(0, 10).split("-").map(Number);
    return new Date(y, m - 1, d).getTime();
  }
  const d = val instanceof Date ? val : new Date(val);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/**
 * Angular-compatible date bound check (filter-sort.service checkDate).
 * Missing row dates pass (same as Angular when invert is false).
 */
export function matchesDateBound(val, bound) {
  if (!bound) return true;
  const day = toDayMs(val);
  if (day == null) return true;
  if (bound.equals != null && bound.equals !== "") {
    const eq = toDayMs(bound.equals);
    return eq != null && day === eq;
  }
  const above =
    bound.above != null && bound.above !== "" ? toDayMs(bound.above) : null;
  const below =
    bound.below != null && bound.below !== "" ? toDayMs(bound.below) : null;
  if (above != null && below != null) {
    return day >= above && day <= below;
  }
  if (above != null) return day >= above;
  if (below != null) return day <= below;
  return true;
}

export function buildDateBoundFromOperator(operator, value, value2) {
  if (!operator || value === "" || value == null) return null;
  if (operator === "equals") return { equals: value };
  if (operator === "above") return { above: value };
  if (operator === "below") return { below: value };
  if (operator === "between") {
    if (value2 === "" || value2 == null) return null;
    const a = toDayMs(value);
    const b = toDayMs(value2);
    if (a == null || b == null) return null;
    return a <= b
      ? { above: value, below: value2 }
      : { above: value2, below: value };
  }
  return null;
}

export function matchesStringFilter(val, operator, match) {
  const s = val == null ? "" : String(val);
  const m = match == null ? "" : String(match);
  if (operator === "equals") return s === m;
  if (operator === "contains") {
    if (!m) return true;
    return s.toLowerCase().includes(m.toLowerCase());
  }
  if (operator === "regex") {
    try {
      return new RegExp(m, "i").test(s);
    } catch {
      return false;
    }
  }
  return true;
}

export function matchesBooleanFilter(val, match) {
  const expected = match === true || match === "true";
  return Boolean(val) === expected;
}

/** Fields for Rentals Advanced Filters modal (Angular ECR DEFAULT_PARAMS_RENTAL). */
export const RENTAL_ADVANCED_FILTER_FIELDS = [
  {
    value: "customerName",
    label: "Customer Name",
    type: "string",
    group: "Customer",
  },
  {
    value: "autoPayment",
    label: "Customer Auto Payment",
    type: "boolean",
    group: "Customer",
  },
  {
    value: "isBusiness",
    label: "Is Business Customer",
    type: "boolean",
    group: "Customer",
  },
  {
    value: "accountType",
    label: "Rental Type",
    type: "string",
    group: "Customer",
  },
  {
    value: "salesChannel",
    label: "Sales Channel",
    type: "string",
    group: "Customer",
  },

  {
    value: "eligible",
    label: "Eligible For Change",
    type: "boolean",
    group: "Contract",
  },
  {
    value: "isProposed",
    label: "Change is Proposed",
    type: "boolean",
    group: "Contract",
  },
  { value: "sqm", label: "SQM", type: "number", group: "Contract" },
  {
    value: "lengthOfStay",
    label: "Length of Stay (Months)",
    type: "number",
    group: "Contract",
  },
  {
    value: "occupiedRate",
    label: "Occupied Rate",
    type: "number",
    group: "Contract",
    unitMode: "rate",
  },
  {
    value: "occupiedRent",
    label: "Occupied Rent",
    type: "number",
    group: "Contract",
    unitMode: "rent",
  },
  {
    value: "streetRate",
    label: "Street Rate",
    type: "number",
    group: "Contract",
    unitMode: "rate",
  },
  {
    value: "streetRent",
    label: "Street Rent",
    type: "number",
    group: "Contract",
    unitMode: "rent",
  },
  {
    value: "occVStreetPerc",
    label: "Occ. Rent V. Street Rent %",
    type: "number",
    group: "Contract",
  },
  {
    value: "snoozeUntil",
    label: "Next Eligible On",
    type: "date",
    group: "Contract",
  },
  {
    value: "lastPriceChangeOn",
    label: "Last Changed On",
    type: "date",
    group: "Contract",
  },
  {
    value: "startOn",
    label: "Contract Start On",
    type: "date",
    group: "Contract",
  },
  {
    value: "moveInDate",
    label: "Move In Date",
    type: "date",
    group: "Contract",
  },
  {
    value: "moveInRate",
    label: "Move In Rate",
    type: "number",
    group: "Contract",
    unitMode: "rate",
  },
  {
    value: "moveInRent",
    label: "Move In Rent",
    type: "number",
    group: "Contract",
    unitMode: "rent",
  },
  {
    value: "discountRate",
    label: "Discount Rate",
    type: "number",
    group: "Contract",
    unitMode: "rate",
  },
  {
    value: "discountRent",
    label: "Discount Rent",
    type: "number",
    group: "Contract",
    unitMode: "rent",
  },
  {
    value: "discountPerc",
    label: "Discount %",
    type: "number",
    group: "Contract",
  },
  {
    value: "description",
    label: "Description",
    type: "string",
    group: "Contract",
  },

  {
    value: "recommendedRate",
    label: "Recommended Rate",
    type: "number",
    group: "Recommendation",
    unitMode: "rate",
  },
  {
    value: "recommendedPrice",
    label: "Recommended Rent",
    type: "number",
    group: "Recommendation",
    unitMode: "rent",
  },
  {
    value: "finalRate",
    label: "Final Rate",
    type: "number",
    group: "Recommendation",
    unitMode: "rate",
  },
  {
    value: "finalRent",
    label: "Final Rent",
    type: "number",
    group: "Recommendation",
    unitMode: "rent",
  },
  {
    value: "overrideRate",
    label: "Override Rate",
    type: "number",
    group: "Recommendation",
    unitMode: "rate",
  },
  {
    value: "overrideValue",
    label: "Override Rent",
    type: "number",
    group: "Recommendation",
    unitMode: "rent",
  },
  {
    value: "recommendedIncPerc",
    label: "Recommended Inc. %",
    type: "number",
    group: "Recommendation",
  },
];

/**
 * Advanced filter fields for the current Unit / m² toggle.
 * Rate fields when `isRate` (m²); Rent fields when Unit. Untagged fields always show.
 * Existing chips for the other mode still apply until cleared.
 */
export function getRentalAdvancedFilterFields(isRate) {
  const mode = isRate ? "rate" : "rent";
  return RENTAL_ADVANCED_FILTER_FIELDS.filter(
    (f) => !f.unitMode || f.unitMode === mode,
  );
}

const FIELD_TYPE = Object.fromEntries(
  RENTAL_ADVANCED_FILTER_FIELDS.map((f) => [f.value, f.type]),
);

export function getRentalAdvancedFieldValue(row, field) {
  if (!row) return undefined;
  switch (field) {
    case "customerName":
      return row.customerName === "—" ? "" : (row.customerName ?? "");
    case "autoPayment":
      return Boolean(row.autoPayment);
    case "isBusiness":
      return Boolean(row.isBusiness);
    case "accountType":
      return row.accountType ?? "";
    case "salesChannel":
      return row.salesChannel ?? "";
    case "eligible":
      return Boolean(row.eligible);
    case "isProposed":
      return Boolean(row.isProposed);
    case "description":
      return row.description ?? "";
    case "snoozeUntil":
      return row.snoozeUntil || null;
    case "lastPriceChangeOn":
      return row.lastPriceChangeOn || null;
    case "startOn":
      return row.startOn || null;
    case "moveInDate":
      return row.moveInDate || null;
    case "discountRent":
      return Number(row.discountRent ?? row.discount ?? 0);
    case "recommendedPrice":
      return Number(row.recommendedPrice);
    default:
      return Number(row[field]);
  }
}

export function isAdvancedFilterEnabled(filter) {
  return filter?.enabled !== false;
}

export function rowMatchesAdvancedFilter(row, filter) {
  if (!filter) return true;
  if (!isAdvancedFilterEnabled(filter)) return true;
  const type = filter.type || FIELD_TYPE[filter.field] || "number";
  const raw = getRentalAdvancedFieldValue(row, filter.field);

  if (type === "number") {
    const bound = buildNumberBoundFromOperator(
      filter.operator,
      filter.value,
      filter.value2,
    );
    if (!bound) return true;
    return matchesNumberBound(Number(raw), bound);
  }
  if (type === "date") {
    const bound = buildDateBoundFromOperator(
      filter.operator,
      filter.value,
      filter.value2,
    );
    if (!bound) return true;
    return matchesDateBound(raw, bound);
  }
  if (type === "string") {
    return matchesStringFilter(raw, filter.operator, filter.value);
  }
  if (type === "choice") {
    return String(raw ?? "") === String(filter.value ?? "");
  }
  if (type === "boolean") {
    return matchesBooleanFilter(raw, filter.value);
  }
  return true;
}

/** All enabled advanced filters must match (AND), same as stacking Angular chips. */
export function applyAdvancedFilters(rows, advancedFilters) {
  if (!advancedFilters?.length) return rows;
  const active = advancedFilters.filter(isAdvancedFilterEnabled);
  if (!active.length) return rows;
  return rows.filter((row) =>
    active.every((f) => rowMatchesAdvancedFilter(row, f)),
  );
}
